import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { AppLayout } from "@/components/AppLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Users, GraduationCap, UserCircle, Receipt, Activity } from "lucide-react";

function StatCard({ label, value, sub, icon }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="eyebrow text-[9px] text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      <div className="mt-3 font-brand text-3xl font-light">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    const [st, t, p, s, inv] = await Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/users?role=teacher"),
      api.get("/users?role=parent"),
      api.get("/students"),
      api.get("/invoices"),
    ]);
    if (st.status === "fulfilled") setStats(st.value.data);
    if (t.status === "fulfilled") setTeachers(t.value.data);
    if (p.status === "fulfilled") setParents(p.value.data);
    if (s.status === "fulfilled") setStudents(s.value.data);
    else toast.error("Could not load students");
    if (inv.status === "fulfilled") setInvoices(inv.value.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

  return (
    <AppLayout title="Business Overview" subtitle="Manage your staff, students, families and invoicing.">
      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><Activity className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="tutors" data-testid="tab-tutors"><Users className="h-4 w-4 mr-1.5" />Tutors</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-parents"><UserCircle className="h-4 w-4 mr-1.5" />Parents</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students"><GraduationCap className="h-4 w-4 mr-1.5" />Students</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="h-4 w-4 mr-1.5" />Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard label="Active Tutors" value={stats.teachers} icon={<Users className="h-4 w-4" />} />
              <StatCard label="Students" value={stats.students} icon={<GraduationCap className="h-4 w-4" />} />
              <StatCard label="Sessions This Week" value={stats.sessions_week} sub={`${stats.total_sessions} all time`} icon={<Activity className="h-4 w-4" />} />
              <StatCard label="Families" value={stats.parents} icon={<UserCircle className="h-4 w-4" />} />
              <StatCard label="Revenue (paid)" value={fmt(stats.revenue)} sub={`${stats.paid_invoices} invoices`} icon={<Receipt className="h-4 w-4" />} />
              <StatCard label="Outstanding" value={fmt(stats.outstanding)} sub={`${stats.pending_invoices} pending`} icon={<Receipt className="h-4 w-4" />} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="tutors">
          <UserSection role="teacher" users={teachers} onChange={load} />
        </TabsContent>
        <TabsContent value="parents">
          <UserSection role="parent" users={parents} onChange={load} />
        </TabsContent>
        <TabsContent value="students">
          <StudentSection students={students} parents={parents} teachers={teachers} onChange={load} />
        </TabsContent>
        <TabsContent value="invoices">
          <InvoiceSection invoices={invoices} parents={parents} students={students} onChange={load} fmt={fmt} />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}

function UserSection({ role, users, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const label = role === "teacher" ? "Tutor" : "Parent";

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", { ...form, role });
      toast.success(`${label} added`);
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success(`${label} removed`);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid={`add-${role}-button`}>
              <Plus className="h-4 w-4 mr-1.5" /> Add {label}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add {label}</DialogTitle>
              <DialogDescription>Create a sign-in for this {label.toLowerCase()}.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${role}-name-input`} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${role}-email-input`} />
              </div>
              <div className="space-y-2">
                <Label>Temporary password</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid={`${role}-password-input`} />
                <p className="text-xs text-muted-foreground">Share this with the {label.toLowerCase()}; they can reset it later.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.name || !form.email || form.password.length < 6} className="rounded-full" data-testid={`save-${role}-button`}>
                {saving ? "Saving…" : `Add ${label}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`admin-${role}-table`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-10">No {label.toLowerCase()}s yet.</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground font-mono-tt text-xs">{u.email}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`delete-${role}-button`}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StudentSection({ students, parents, teachers, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", grade: "", parent_id: "", teacher_ids: [] });
  const [saving, setSaving] = useState(false);

  const toggleTeacher = (id) =>
    setForm((f) => ({
      ...f,
      teacher_ids: f.teacher_ids.includes(id) ? f.teacher_ids.filter((t) => t !== id) : [...f.teacher_ids, id],
    }));

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/students", { ...form, parent_id: form.parent_id || null });
      toast.success("Student added");
      setOpen(false);
      setForm({ name: "", grade: "", parent_id: "", teacher_ids: [] });
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/students/${id}`);
    toast.success("Student removed");
    onChange();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-student-button"><Plus className="h-4 w-4 mr-1.5" /> Add Student</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Student</DialogTitle>
              <DialogDescription>Add a student and assign their parent and tutors.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Student name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Year / grade</Label>
                <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g. Year 6" />
              </div>
              <div className="space-y-2">
                <Label>Parent</Label>
                <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                  <SelectTrigger data-testid="student-parent-select"><SelectValue placeholder="Assign a parent" /></SelectTrigger>
                  <SelectContent>
                    {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigned tutors</Label>
                <div className="flex flex-wrap gap-2">
                  {teachers.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`assign-tutor-${t.id}`}
                      onClick={() => toggleTeacher(t.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${form.teacher_ids.includes(t.id) ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"}`}
                    >
                      {t.name}
                    </button>
                  ))}
                  {teachers.length === 0 && <span className="text-xs text-muted-foreground">Add tutors first.</span>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.name} className="rounded-full" data-testid="save-student-button">
                {saving ? "Saving…" : "Add Student"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead>Tutors</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No students yet.</TableCell></TableRow>
            ) : students.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground">{s.grade || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{s.parent?.name || <span className="text-amber-600">Unassigned</span>}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{s.teachers.map((t) => t.name).join(", ") || "—"}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid="delete-student-button"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InvoiceSection({ invoices, parents, students, onChange, fmt }) {
  const [open, setOpen] = useState(false);
  const [parentId, setParentId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [saving, setSaving] = useState(false);

  const total = items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const parentStudents = students.filter((s) => s.parent_id === parentId);

  const setItem = (idx, key, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((a) => [...a, { description: "", amount: "" }]);
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/invoices", {
        parent_id: parentId,
        student_id: studentId || null,
        due_date: dueDate || null,
        items: items.filter((i) => i.description && parseFloat(i.amount) > 0).map((i) => ({ description: i.description, amount: parseFloat(i.amount) })),
      });
      toast.success("Invoice generated & sent to parent");
      setOpen(false);
      setParentId(""); setStudentId(""); setDueDate(""); setItems([{ description: "", amount: "" }]);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    onChange();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="admin-generate-invoice-btn"><Plus className="h-4 w-4 mr-1.5" /> Generate Invoice</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Generate Invoice</DialogTitle>
              <DialogDescription>Create an itemised invoice to send to a parent.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Parent / family</Label>
                <Select value={parentId} onValueChange={(v) => { setParentId(v); setStudentId(""); }}>
                  <SelectTrigger data-testid="invoice-parent-select"><SelectValue placeholder="Select parent" /></SelectTrigger>
                  <SelectContent>{parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {parentId && (
                <div className="space-y-2">
                  <Label>Student (optional)</Label>
                  <Select value={studentId} onValueChange={setStudentId}>
                    <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>{parentStudents.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Due date (optional)</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Line items</Label>
                {items.map((it, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input placeholder="Description (e.g. 4 × 1hr Maths)" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="flex-1" data-testid={`invoice-item-desc-${idx}`} />
                    <Input placeholder="£" type="number" value={it.amount} onChange={(e) => setItem(idx, "amount", e.target.value)} className="w-24" data-testid={`invoice-item-amount-${idx}`} />
                    {items.length > 1 && <Button variant="ghost" size="icon" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} className="rounded-full" data-testid="invoice-add-line-button"><Plus className="h-3 w-3 mr-1" /> Add line</Button>
              </div>
              <div className="flex justify-between items-center border-t border-border pt-3">
                <span className="eyebrow text-[10px] text-muted-foreground">Total</span>
                <span className="font-brand text-2xl font-light">{fmt(total)}</span>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !parentId || total <= 0} className="rounded-full" data-testid="save-invoice-button">
                {saving ? "Generating…" : "Generate & Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No invoices yet.</TableCell></TableRow>
            ) : invoices.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell className="font-mono-tt text-xs">{inv.invoice_number}</TableCell>
                <TableCell>{inv.parent_name}</TableCell>
                <TableCell className="font-medium">{fmt(inv.total)}</TableCell>
                <TableCell><StatusBadge status={inv.status} /></TableCell>
                <TableCell className="text-muted-foreground text-xs">{inv.due_date || "—"}</TableCell>
                <TableCell>
                  {inv.status !== "paid" && <Button variant="ghost" size="icon" onClick={() => remove(inv.id)} data-testid="delete-invoice-button"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
